import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";
import {
  CreateJournalEntrySchema,
  UpdateJournalEntrySchema,
} from "./journal-entry.schema";

// ── Shared input schemas ──────────────────────────────────────────────────────

export const JournalEntryIdInputSchema = z.object({
  id: z.string().trim().min(1),
  businessId: z.string().trim().min(1),
});

export const ListJournalEntriesInputSchema = z.object({
  businessId: z.string().trim().min(1),
  search: z.string().optional(),
  accountId: z.string().trim().min(1).optional(),
  offset: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(200).optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  ids: z.array(z.string().trim().min(1)).max(500).optional(),
  classIds: z.array(z.string().trim().min(1)).max(500).optional(),
  source: z.enum(["manual", "bank"]).optional(),
  sortBy: z.string().optional(),
  sortDir: z.enum(["asc", "desc"]).optional(),
});

export const GetJournalEntryInputSchema = z.object({
  businessId: z.string().trim().min(1),
  id: z.string().trim().min(1),
});

export const CreateJournalEntryInputSchema = z.object({
  businessId: z.string().trim().min(1),
  item: CreateJournalEntrySchema,
});

export const UpdateJournalEntryInputSchema = z.object({
  ...JournalEntryIdInputSchema.shape,
  updates: UpdateJournalEntrySchema,
});

export const BulkDeleteJournalEntriesInputSchema = z.object({
  businessId: z.string().trim().min(1),
  ids: z.array(z.string().trim().min(1)).min(1).max(200),
});

// ── Server functions ──────────────────────────────────────────────────────────

export const listJournalEntries = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => ListJournalEntriesInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("journalEntries.list", async () => {
      const { listJournalEntriesForUser } =
        await import("./journal-entry.server");
      return listJournalEntriesForUser(context.user.id, data.businessId, {
        search: data.search,
        accountId: data.accountId,
        offset: data.offset,
        limit: data.limit,
        dateFrom: data.dateFrom,
        dateTo: data.dateTo,
        ids: data.ids,
        classIds: data.classIds,
        source: data.source,
        sortBy: data.sortBy,
        sortDir: data.sortDir,
      });
    });
  });

export const getJournalEntry = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => GetJournalEntryInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("journalEntries.get", async () => {
      const { getJournalEntryForUser } = await import("./journal-entry.server");
      const result = await getJournalEntryForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const createJournalEntry = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => CreateJournalEntryInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("journalEntries.create", async () => {
      const { createJournalEntryForUser } =
        await import("./journal-entry.server");
      return createJournalEntryForUser(
        context.user.id,
        data.businessId,
        data.item,
      );
    });
  });

export const updateJournalEntry = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => UpdateJournalEntryInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("journalEntries.update", async () => {
      const { updateJournalEntryForUser } =
        await import("./journal-entry.server");
      const result = await updateJournalEntryForUser(
        context.user.id,
        data.id,
        data.updates,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const deleteJournalEntry = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => JournalEntryIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("journalEntries.delete", async () => {
      const { deleteJournalEntryForUser } =
        await import("./journal-entry.server");
      const result = await deleteJournalEntryForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const bulkDeleteJournalEntries = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => BulkDeleteJournalEntriesInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("journalEntries.bulkDelete", async () => {
      const { bulkDeleteJournalEntriesForUser } =
        await import("./journal-entry.server");
      return bulkDeleteJournalEntriesForUser(
        context.user.id,
        data.businessId,
        data.ids,
      );
    });
  });
